import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { parseUnits } from "viem";
import { useUnifiedWallet } from "~/hooks/useUnifiedWallet";
import { SwapCard, type SwapSide } from "~/components/SwapCard/SwapCard";
import { RouteList } from "~/components/RouteList/RouteList";
import { RouteCard } from "~/components/RouteList/RouteCard";
import { TransactionStatus } from "~/components/TransactionStatus/TransactionStatus";
import { useStreamingRoutes } from "~/hooks/useStreamingRoutes";
import { usePreferences } from "~/hooks/usePreferences";
import { useExecuteSteps } from "~/hooks/useExecuteSteps";
import { useChains } from "~/hooks/useChains";
import { useTokenList } from "~/hooks/useTokenList";
import { resolveSlippage } from "~/lib/constants";
import { getRouteScoreUsd } from "~/lib/routeMetrics";
import type { ChainMeta, TokenMeta, Route, ChainType } from "~/api/types";
import { Dialog, DialogClose, DialogTitle } from "~/components/shared/Dialog";
import { RecipientInput } from "~/components/SwapCard/RecipientInput";
import { isValidAddress } from "~/lib/address";

export function SwapPage() {
  const { address } = useUnifiedWallet();
  const { prefs } = usePreferences();
  const { chains } = useChains();

  const [from, setFrom] = useState<SwapSide>({
    chain: null,
    token: null,
    amount: "",
  });
  const [to, setTo] = useState<SwapSide>({
    chain: null,
    token: null,
    amount: "",
  });

  // Pre-select defaults: Ethereum ETH → Arbitrum USDC
  // Only fetch defaults until chains are selected
  const { tokens: ethTokens } = useTokenList(from.chain ? undefined : "1");
  const { tokens: arbTokens } = useTokenList(to.chain ? undefined : "42161");

  useEffect(() => {
    if (from.chain || !chains.length) return;
    const eth = chains.find((c) => c.chainId === "1");
    const arb = chains.find((c) => c.chainId === "42161");
    const ethToken = ethTokens.find((t) => t.symbol === "ETH");
    const arbToken = arbTokens.find((t) => t.symbol === "USDC");
    // Wait for both token lists before setting defaults — otherwise
    // setting from.chain early locks out the to side permanently
    if (!eth || !ethToken || !arb || !arbToken) return;
    setFrom({ chain: eth, token: ethToken, amount: "" });
    setTo({ chain: arb, token: arbToken, amount: "" });
  }, [chains, ethTokens, arbTokens, from.chain]);
  // Provider-name-based selection: survives route ID changes across refreshes.
  // When null, auto-selects best route (routes[0]).
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const lastPinnedOutputRef = useRef<bigint | null>(null);
  const [isMobileRoutesOpen, setIsMobileRoutesOpen] = useState(false);

  // Listen for swap-prefill events from WalletDrawer Buy/Sell buttons
  useEffect(() => {
    const handler = (e: Event) => {
      const { side, chain, token } = (e as CustomEvent<{
        side: "from" | "to";
        chain: ChainMeta;
        token: TokenMeta;
      }>).detail;
      if (side === "from") {
        setFrom({ chain, token, amount: "" });
      } else {
        setTo({ chain, token, amount: "" });
      }
      setSelectedProvider(null);
    };
    window.addEventListener("swap-prefill", handler);
    return () => window.removeEventListener("swap-prefill", handler);
  }, []);

  const canQuote =
    from.chain && from.token && to.chain && to.token && from.amount && from.amount !== "0";

  const amountInSmallestUnit = useMemo(() => {
    if (!canQuote || from.token?.decimals == null) return null;
    try {
      return parseUnits(from.amount, from.token.decimals).toString();
    } catch {
      return null;
    }
  }, [canQuote, from.amount, from.token?.decimals]);

  const isCrossChain = from.chain && to.chain && from.chain.chainId !== to.chain.chainId;

  // Cross-family bridging: different address formats on source vs destination
  const fromChainType: ChainType = from.chain?.chainType ?? "evm";
  const toChainType: ChainType = to.chain?.chainType ?? "evm";
  const isCrossFamily = from.chain && to.chain && fromChainType !== toChainType;
  const [recipient, setRecipient] = useState("");
  const recipientValid = isCrossFamily && recipient ? isValidAddress(recipient, toChainType) : false;

  // Clear recipient when chains change
  useEffect(() => {
    setRecipient("");
  }, [fromChainType, toChainType]);

  const streamParams = canQuote && amountInSmallestUnit && amountInSmallestUnit !== "0"
    ? {
        fromChainId: from.chain!.chainId,
        toChainId: to.chain!.chainId,
        fromToken: from.token!.address,
        toToken: to.token!.address,
        amount: amountInSmallestUnit,
        sender: address ?? undefined,
        recipient: recipientValid ? recipient : undefined,
        slippageBps: resolveSlippage(prefs.slippageBps, !!isCrossChain),
      }
    : null;

  const { stepStates, currentStepIndex, isExecuting, execute, retry, reset } = useExecuteSteps();

  const { routes, pending, isStreaming, isDone, error, secondsUntilRefresh, refresh } =
    useStreamingRoutes(streamParams, { paused: isExecuting });

  // Cache auto-selected route so it persists through refresh cycles
  const autoSelectedRef = useRef<Route | null>(null);
  if (routes.length === 0) autoSelectedRef.current = null;
  const selectedRoute = useMemo(() => {
    // If user pinned a provider, find it by name (survives route ID changes)
    if (selectedProvider) {
      const match = routes.find((r) => r.summary.provider.name === selectedProvider);
      if (match) return match;
      // Provider not found yet (mid-stream) — fall through to auto logic
    }
    // Only auto-select once all providers have responded
    if (isDone && routes[0]) {
      autoSelectedRef.current = routes[0];
      return routes[0];
    }
    // During refresh, keep showing the previous best
    return autoSelectedRef.current;
  }, [routes, selectedProvider, isDone]);

  // Clear provider pin when chain or token changes (not amount — that's too aggressive per keystroke)
  const handleFromChange = useCallback(
    (side: SwapSide) => {
      if (side.chain !== from.chain || side.token !== from.token)
        setSelectedProvider(null);
      setFrom(side);
    },
    [from],
  );
  const handleToChange = useCallback(
    (side: SwapSide) => {
      if (side.chain !== to.chain || side.token !== to.token)
        setSelectedProvider(null);
      setTo(side);
    },
    [to],
  );

  // Stale detection: auto-clear pin if provider disappears or output drops ≥6%.
  // Only runs after streaming completes to avoid false positives mid-stream.
  useEffect(() => {
    if (!isDone || !selectedProvider || routes.length === 0) return;
    const match = routes.find((r) => r.summary.provider.name === selectedProvider);
    if (!match) {
      setSelectedProvider(null);
      lastPinnedOutputRef.current = null;
      return;
    }
    try {
      const current = BigInt(match.summary.toAmountEstimated);
      if (
        lastPinnedOutputRef.current !== null &&
        current * 100n <= lastPinnedOutputRef.current * 94n
      ) {
        setSelectedProvider(null);
        lastPinnedOutputRef.current = null;
        return;
      }
      lastPinnedOutputRef.current = current;
    } catch {
      // malformed amount, skip detection
    }
  }, [isDone, routes, selectedProvider]);

  // Keep a stable ref to the route being executed so it doesn't change
  // when streaming resumes after execution completes
  const executedRouteRef = useRef<Route | null>(null);

  const handleSwapDirection = useCallback(() => {
    const prevFrom = from;
    const prevTo = to;
    setFrom({ chain: prevTo.chain, token: prevTo.token, amount: prevFrom.amount });
    setTo({ chain: prevFrom.chain, token: prevFrom.token, amount: "" });
    setSelectedProvider(null);
  }, [from, to]);

  const handleExecute = useCallback(() => {
    if (!selectedRoute) return;

    // Safety: verify the route's baked-in recipient matches the user's intent
    if (selectedRoute.recipient) {
      const expectedRecipient = isCrossFamily ? recipient : address;
      if (
        expectedRecipient &&
        selectedRoute.recipient.toLowerCase() !== expectedRecipient.toLowerCase()
      ) {
        console.error(
          "[swap] recipient mismatch — route has",
          selectedRoute.recipient,
          "but expected",
          expectedRecipient,
        );
        return;
      }
    }

    executedRouteRef.current = selectedRoute;
    execute(selectedRoute);
  }, [selectedRoute, execute, isCrossFamily, recipient, address]);

  const handleReset = useCallback(() => {
    executedRouteRef.current = null;
    reset();
  }, [reset]);

  const handleRetry = useCallback(() => {
    retry();
  }, [retry]);

  const showTxStatus = stepStates.length > 0 && executedRouteRef.current;
  const bestAmount = useMemo(() => {
    if (!routes.length) return 0n;
    try {
      return BigInt(routes[0].summary.toAmountEstimated);
    } catch {
      return 0n;
    }
  }, [routes]);
  const bestRouteUsd = useMemo(
    () => (routes.length > 0 ? getRouteScoreUsd(routes[0].summary) : null),
    [routes],
  );

  return (
    <div className="mx-auto grid w-full max-w-[62rem] grid-cols-1 items-start gap-6 lg:grid-cols-2">
      <div className="lg:sticky lg:top-6 flex flex-col gap-4">
        <SwapCard
          from={from}
          to={to}
          onFromChange={handleFromChange}
          onToChange={handleToChange}
          onSwapDirection={handleSwapDirection}
          selectedRoute={selectedRoute}
          isStreaming={isStreaming}
          onExecute={handleExecute}
          isExecuting={isExecuting}
          needsRecipient={!!isCrossFamily && !recipientValid}
        />
        {isCrossFamily && to.chain && (
          <RecipientInput
            chainType={toChainType}
            chainName={to.chain.name}
            value={recipient}
            onChange={setRecipient}
          />
        )}
        {!showTxStatus && (selectedRoute || pending.length > 0 || error) && (
          <div className="lg:hidden">
            <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-card-section)] bg-[var(--color-bg-card-section)] p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Route Preview
                  </p>
                  <p className="text-xs text-[var(--color-text-secondary)]">
                    {isStreaming
                      ? "Available routes update live as providers respond."
                      : "Review the route or compare all available options."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMobileRoutesOpen(true)}
                  className="shrink-0 rounded-full border border-[var(--color-border-card-section)] px-3 py-1.5 text-xs font-semibold text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-route-hover)]"
                >
                  Show All Routes
                </button>
              </div>

              {selectedRoute ? (
                <RouteCard
                  route={selectedRoute}
                  isSelected
                  isFirst={routes[0]?.routeId === selectedRoute.routeId}
                  bestAmount={bestAmount}
                  bestRouteUsd={bestRouteUsd}
                  onClick={() => setIsMobileRoutesOpen(true)}
                />
              ) : (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {error ? error.message : "Finding routes..."}
                </p>
              )}
            </div>

            <Dialog open={isMobileRoutesOpen} onOpenChange={setIsMobileRoutesOpen} size="lg">
              <DialogTitle>Routes</DialogTitle>
              <DialogClose />
              <RouteList
                routes={routes}
                pending={pending}
                isStreaming={isStreaming}
                isDone={isDone}
                selectedRouteId={selectedRoute?.routeId ?? null}
                onSelect={(provider) => {
                  setSelectedProvider(provider);
                  setIsMobileRoutesOpen(false);
                }}
                error={error}
                secondsUntilRefresh={secondsUntilRefresh}
                onRefresh={refresh}
              />
            </Dialog>
          </div>
        )}
        {/* Mobile: show tx status below card */}
        {showTxStatus && (
          <div className="lg:hidden">
            <TransactionStatus
              route={executedRouteRef.current!}
              from={from}
              to={to}
              stepStates={stepStates}
              currentIndex={currentStepIndex}
              onRetry={handleRetry}
              onReset={handleReset}
            />
          </div>
        )}
      </div>
      <div className="hidden lg:block">
        {/* Desktop: tx status replaces route list during execution */}
        {showTxStatus ? (
          <TransactionStatus
            route={executedRouteRef.current!}
            from={from}
            to={to}
            stepStates={stepStates}
            currentIndex={currentStepIndex}
            onRetry={handleRetry}
            onReset={handleReset}
          />
        ) : (
          <RouteList
            routes={routes}
            pending={pending}
            isStreaming={isStreaming}
            isDone={isDone}
            selectedRouteId={selectedRoute?.routeId ?? null}
            onSelect={setSelectedProvider}
            error={error}
            secondsUntilRefresh={secondsUntilRefresh}
            onRefresh={refresh}
          />
        )}
      </div>
    </div>
  );
}
